import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import TrialBanner from "@/components/TrialBanner";
import ProgressChart from "@/components/ProgressChart";
import ReferralCard from "@/components/ReferralCard";
import PromoBanner from "@/components/PromoBanner";
import BadgesGrid from "@/components/BadgesGrid";
import ReminderPrompt from "@/components/ReminderPrompt";
import AdaptivePicks from "@/components/AdaptivePicks";
import MistakeTile from "@/components/MistakeTile";
import StreakCalendar from "@/components/StreakCalendar";
import {
  Headphones, Scale, BookOpen, ArrowRight, Keyboard, ListMusic, Radio,
  Flame, Target, Activity, Trophy, TrendingUp, Sparkles, Crown, Check,
  BarChart3, Award, Gamepad2, FileText, Calendar, Zap, Gavel, Building2,
  MapPin, Landmark, Mic2, Rocket, Book, Library, Users,
} from "lucide-react";

// ============ Hero quick-action pills ============
const QUICK_ACTIONS = [
  { icon: BarChart3, label: "Analytics", to: "/dashboard#chart", tint: "blue" },
  { icon: FileText, label: "Report Card", to: "/attempts", tint: "purple" },
  { icon: Users, label: "Race Groups", to: "/groups", tint: "emerald" },
  { icon: Gamepad2, label: "Speed Race", to: "/live-tests", tint: "crimson" },
];

// ============ Exam Hub — three primary paths (BIG showcase cards) ============
const EXAM_HUB = [
  {
    to: "/legal",
    icon: Scale,
    tag: "Exam-oriented",
    title: "Legal Formatting Typing",
    subtitle: "High Court & Supreme Court judgements",
    features: [
      "500+ exam-oriented judgements",
      "Real 15-min exam simulation",
      "Bold / italic / align mistake analysis",
      "Formatting fidelity scoring",
      "Leaderboard & rank tracking",
    ],
    cta: "Start Practice",
    accent: "emerald",
  },
  {
    to: "/pitman",
    icon: BookOpen,
    tag: "Book 1 – 231 exercises",
    title: "Pitman Shorthand",
    subtitle: "Every chapter · Side-by-side transcription",
    features: [
      "Exercises 1 – 231 (all chapters)",
      "Pitman English version",
      "Book revision playlists",
      "Per-chapter analytics",
      "Chapter-wise leaderboard",
    ],
    cta: "Start Practice",
    accent: "gold",
  },
  {
    to: "/typing",
    icon: Keyboard,
    tag: "State recruitment",
    title: "UP Police Typing",
    subtitle: "Computer Operator · ASI · Confidential",
    features: [
      "Hindi Mangal typing (25 WPM target)",
      "English typing (30 WPM target)",
      "5-min real exam simulation",
      "Live accuracy + punctuation scoring",
      "State-pattern mock tests",
    ],
    cta: "Start UP Police",
    accent: "purple",
  },
];

// ============ Dictation & transcription categories ============
// Allahabad HC is a "combo" — it exposes BOTH Pitman and Legal Formatting sub-links.
const DICTATION_CATEGORIES = [
  { icon: Scale, title: "Court Dictations", desc: "Legal & judicial dictation practice", to: "/dictation?cat=court", tint: "rose" },
  {
    icon: Building2, title: "Allahabad High Court", desc: "AHC · Pitman + Legal Formatting",
    combo: [
      { label: "Pitman AHC", to: "/pitman?court=ahc" },
      { label: "Legal AHC", to: "/legal?court=ahc" },
    ],
    tint: "amber",
  },
  { icon: Landmark, title: "Rajasthan High Court", desc: "Rajasthan HC dictations", to: "/dictation?cat=rhc", tint: "crimson" },
  { icon: MapPin, title: "Delhi High Court", desc: "Delhi HC dictation set", to: "/dictation?cat=dhc", tint: "purple" },
  { icon: Mic2, title: "GCC Maharashtra", desc: "GCC Maharashtra dictation", to: "/dictation?cat=gcc", tint: "emerald" },
  { icon: Headphones, title: "KC Dictations", desc: "Kailash Chandra 400–1000 wpm", to: "/dictation?cat=kc", tint: "gold" },
  { icon: TrendingUp, title: "Progressive Dictations", desc: "Gradually increasing speed", to: "/dictation?cat=progressive", tint: "blue" },
  { icon: Mic2, title: "General Dictations", desc: "All-purpose dictation practice", to: "/dictation", tint: "rose" },
  { icon: Rocket, title: "Speed Booster", desc: "High-speed focused dictation", to: "/dictation?cat=speed", tint: "amber" },
  { icon: Book, title: "1000 Words", desc: "Vocabulary-based transcription", to: "/dictation?cat=1000", tint: "emerald" },
  { icon: Library, title: "Legal Terms", desc: "Legal terminology drills", to: "/dictation?cat=legal-terms", tint: "purple" },
  { icon: Zap, title: "Hindi Shorthand", desc: "आशु लिपिक · Hindi TTS", to: "/hindi-dictation", tint: "crimson" },
];

const MODULES = [
  { to: "/live-tests", icon: Radio, tag: "Live · Weekly", title: "Live Tests", desc: "Fixed-time competitions", spotlight: true, accent: "crimson" },
  { to: "/playlists", icon: ListMusic, tag: "Programs", title: "Playlists", desc: "Curated by your instructor", accent: "purple" },
  { to: "/dictation", icon: Headphones, tag: "Module 01", title: "Audio Dictation", desc: "English · 5 min audio · 45 min transcription", accent: "gold" },
  { to: "/legal", icon: Scale, tag: "Module 02", title: "Legal Typing", desc: "Judgement formatting · WPM", accent: "gold" },
  { to: "/pitman", icon: BookOpen, tag: "Module 03", title: "Pitman Steno", desc: "Book exercises · Split view", accent: "purple" },
  { to: "/typing", icon: Keyboard, tag: "Module 04", title: "Typing Speed", desc: "English + हिन्दी · 1-30 min", accent: "emerald" },
  { to: "/hindi-dictation", icon: Headphones, tag: "Module 05", title: "आशु लिपिक · Hindi Shorthand", desc: "हिंदी श्रुतलेख · 5 min audio · 45 min transcription", accent: "crimson" },
];

const ACCENT_CLASSES = {
  gold: { icon: "bg-brand-gold/15 text-brand-gold border-brand-gold/40", strip: "bg-gradient-to-r from-[#E5C158] to-[#D4AF37]", ring: "hover:border-brand-gold/60" },
  crimson: { icon: "bg-brand-crimson/15 text-brand-crimson border-brand-crimson/40", strip: "bg-gradient-to-r from-brand-crimson to-brand-gold", ring: "hover:border-brand-crimson/60" },
  purple: { icon: "bg-brand-purple/15 text-brand-purple border-brand-purple/40", strip: "bg-gradient-to-r from-brand-purple to-brand-gold", ring: "hover:border-brand-purple/60" },
  emerald: { icon: "bg-brand-emerald/15 text-brand-emerald border-brand-emerald/40", strip: "bg-gradient-to-r from-brand-emerald to-brand-gold", ring: "hover:border-brand-emerald/60" },
  rose: { icon: "bg-rose-500/15 text-rose-400 border-rose-500/40" },
  amber: { icon: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  blue: { icon: "bg-blue-500/15 text-blue-400 border-blue-500/40" },
};

const HUB_ACCENT = {
  emerald: { strip: "from-brand-emerald to-teal-400", btn: "bg-brand-emerald hover:bg-emerald-600 text-white", ring: "border-brand-emerald/40 hover:border-brand-emerald" },
  gold: { strip: "from-[#E5C158] to-[#D4AF37]", btn: "bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A]", ring: "border-brand-gold/40 hover:border-brand-gold" },
  purple: { strip: "from-brand-purple to-fuchsia-500", btn: "bg-brand-purple hover:bg-violet-700 text-white", ring: "border-brand-purple/40 hover:border-brand-purple" },
};

function BigStat({ testId, label, value, unit, icon: Icon, hint, tone = "gold", isFire }) {
  const tones = {
    gold: "text-brand-gold border-brand-gold/40 bg-brand-gold/10",
    blue: "text-blue-400 border-blue-500/40 bg-blue-500/10",
    emerald: "text-brand-emerald border-brand-emerald/40 bg-brand-emerald/10",
    crimson: "text-brand-crimson border-brand-crimson/40 bg-brand-crimson/10",
  };
  return (
    <div className="aurora-stat rounded-2xl p-6 relative" data-testid={testId}>
      <div className="relative flex items-start justify-between mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">{label}</div>
        <div className={`w-10 h-10 border rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className={`w-4 h-4 ${isFire ? "flame-pulse" : ""}`} strokeWidth={2.5} />
        </div>
      </div>
      <div className="relative flex items-baseline gap-2">
        <div className="font-mono text-4xl sm:text-5xl font-black tracking-tight text-foreground">{value ?? 0}</div>
        {unit && <div className="text-sm font-mono text-muted-foreground uppercase tracking-widest">{unit}</div>}
      </div>
      {hint && <div className="relative text-[11px] font-mono text-muted-foreground mt-2">{hint}</div>}
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Good night";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/me").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const displayName = user?.name || user?.email?.split("@")[0] || "Aspirant";
  const streak = stats?.streak_days ?? 0;
  const acc = stats?.avg_accuracy ?? 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PromoBanner />
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <TrialBanner />
        <ReminderPrompt />
        <MistakeTile />

        {/* ============ GREETING HERO with quick actions ============ */}
        <div className="relative mb-8 animate-fade-up rounded-3xl overflow-hidden border border-brand-gold/25 p-6 sm:p-10" data-testid="dashboard-greeting">
          <div className="absolute inset-0 aurora-bg pointer-events-none" />
          <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/40 bg-brand-gold/10 rounded-full text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">
                  <Sparkles className="w-3 h-3" /> {greetingByHour()}
                </div>
                {streak >= 7 && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 border border-brand-crimson/40 bg-brand-crimson/10 rounded-full text-[10px] uppercase tracking-[0.3em] font-bold text-brand-crimson">
                    <Flame className="w-3 h-3 flame-pulse" /> {streak}-day streak
                  </div>
                )}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-3" data-testid="dashboard-title">
                {greetingByHour()}, <span className="gold-text-soft">{displayName}</span>.
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
                Let's continue your preparation. Pick a module below or resume where you left off — every session updates your streak and unlocks adaptive picks.
              </p>
            </div>
            {/* Quick action pills */}
            <div className="grid grid-cols-2 lg:grid-cols-2 gap-2 lg:min-w-[280px]">
              {QUICK_ACTIONS.map((qa) => {
                const Icon = qa.icon;
                const t = ACCENT_CLASSES[qa.tint] || ACCENT_CLASSES.gold;
                return (
                  <Link
                    key={qa.label}
                    to={qa.to}
                    className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl border bg-card/60 hover:bg-card transition-all hover:-translate-y-0.5 ${t.icon.replace("bg-", "hover:bg-").split(" ").filter(c => c.startsWith("hover:")).join(" ")}`}
                    data-testid={`quick-${qa.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${t.icon}`}>
                      <Icon className="w-4 h-4" strokeWidth={2.5} />
                    </div>
                    <span className="text-[11px] uppercase tracking-widest font-black flex-1">{qa.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* ============ PROMO / FEATURE BANNER ============ */}
        <div className="relative mb-10 rounded-3xl overflow-hidden border border-brand-gold/25 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white" data-testid="dashboard-promo">
          <div className="absolute inset-0 aurora-bg opacity-70 pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-6 p-6 sm:p-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/40 bg-brand-gold/10 rounded-full text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">
                <Zap className="w-3 h-3" /> New this week
              </div>
              <h2 className="font-display text-2xl sm:text-4xl font-black tracking-tight leading-tight mb-3 text-white">
                <span className="gold-text-soft">Adaptive drills</span> that push your 88–92 % sweet spot.
              </h2>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed mb-6 max-w-xl">
                Every session is now tuned to your last 5 attempts. Struggle-line mistakes go into a Mistake Queue you can drill anytime. Certificates auto-generate at 60 WPM · 95 % accuracy.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/legal">
                  <button className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-gradient-to-r from-[#E5C158] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#B8860B] text-[#0F172A] text-xs uppercase tracking-widest font-black shadow-lg shadow-brand-gold/25" data-testid="dashboard-quick-start">
                    Start today's session <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </Link>
                <Link to="/badges">
                  <button className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-brand-gold/40 hover:border-brand-gold hover:bg-brand-gold/10 text-xs uppercase tracking-widest font-bold transition-all">
                    <Award className="w-3.5 h-3.5 text-brand-gold" /> See badges
                  </button>
                </Link>
              </div>
            </div>
            {/* Right-side stat rings */}
            <div className="grid grid-cols-2 gap-3">
              <MiniPromoCard icon={Target} label="Avg Accuracy" value={`${acc}%`} tone={acc >= 90 ? "emerald" : "gold"} sub="Rolling average" />
              <MiniPromoCard icon={TrendingUp} label="Avg WPM" value={stats?.avg_wpm ?? 0} tone="gold" sub="Last 30 days" />
              <MiniPromoCard icon={Activity} label="Total Attempts" value={stats?.total_attempts ?? 0} tone="blue" sub="Sessions logged" />
              <MiniPromoCard icon={Flame} label="Day Streak" value={streak} tone={streak >= 7 ? "crimson" : "gold"} sub={streak >= 7 ? "On fire" : "Keep the fire alive"} isFire={streak >= 7} />
            </div>
          </div>
        </div>

        {/* ============ STAT QUAD ============ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <BigStat testId="stat-attempts" label="Total attempts" value={stats?.total_attempts} icon={Activity} tone="blue" hint="Sessions logged" />
          <BigStat testId="stat-streak"   label="Day streak"     value={streak} unit={streak >= 7 ? "days · on fire" : "days"} icon={Flame} tone={streak >= 7 ? "crimson" : "gold"} isFire={streak >= 7} hint={streak >= 30 ? "Legendary consistency" : "Keep the fire alive"} />
          <BigStat testId="stat-accuracy" label="Avg accuracy"   value={acc} unit="%" icon={Target} tone={acc >= 90 ? "emerald" : "gold"} hint={acc >= 95 ? "Certificate-ready" : "Aim for 95%"} />
          <BigStat testId="stat-wpm"      label="Avg WPM"        value={stats?.avg_wpm} unit="wpm" icon={TrendingUp} tone="gold" hint="Rolling average" />
        </div>

        {/* ============ AHC PS EXAM HUB — 3 BIG feature cards ============ */}
        <div className="mb-12" data-testid="dashboard-exam-hub">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2 flex items-center gap-2">
                <Crown className="w-3 h-3" /> Your complete preparation hub
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Exam Hub</h2>
              <p className="text-base text-muted-foreground mt-2 max-w-2xl">All the essential tools you need to crack the Allahabad High Court PS, UP Police, SSC Stenographer and other government stenography exams.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {EXAM_HUB.map((h) => {
              const Icon = h.icon;
              const a = HUB_ACCENT[h.accent];
              return (
                <div key={h.to} className={`group relative rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-card/80 to-card/40 border-2 ${a.ring} transition-all hover:-translate-y-2 overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-brand-gold/15`} data-testid={`exam-hub-${h.to.slice(1)}`}>
                  <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${a.strip}`} />
                  <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br ${a.strip} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`} />
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/10 to-white/0 border-2 border-brand-gold/30 flex items-center justify-center shadow-lg">
                      <Icon className="w-8 h-8 text-brand-gold" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">{h.tag}</div>
                    </div>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-black tracking-tight mb-2 leading-tight">{h.title}</h3>
                  <p className="text-sm text-muted-foreground mb-6">{h.subtitle}</p>
                  <ul className="space-y-3 mb-8">
                    {h.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-[15px] text-foreground/90">
                        <div className="w-5 h-5 shrink-0 mt-0.5 rounded-full bg-brand-emerald/15 border border-brand-emerald/40 flex items-center justify-center">
                          <Check className="w-3 h-3 text-brand-emerald" strokeWidth={4} />
                        </div>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={h.to}>
                    <button className={`w-full h-12 rounded-xl text-xs uppercase tracking-widest font-black shadow-md transition-all ${a.btn}`}>
                      {h.cta} <ArrowRight className="w-4 h-4 ml-2 inline" />
                    </button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {/* ============ DICTATION & TRANSCRIPTION HUB — 12 category tiles ============ */}
        <div className="mb-12" data-testid="dashboard-dictation-hub">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2 flex items-center gap-2">
                <Headphones className="w-3 h-3" /> {DICTATION_CATEGORIES.length} categories
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">English Dictation & Transcription</h2>
              <p className="text-sm text-muted-foreground mt-1">Complete dictation practice covering every category for English stenography.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {DICTATION_CATEGORIES.map((c) => {
              const Icon = c.icon;
              const t = ACCENT_CLASSES[c.tint] || ACCENT_CLASSES.gold;
              if (c.combo) {
                return (
                  <div
                    key={c.title}
                    className="group flex flex-col gap-3 p-4 rounded-xl border border-brand-gold/25 bg-card/50 hover:border-brand-gold/60 hover:-translate-y-0.5 transition-all"
                    data-testid={`cat-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-lg border flex items-center justify-center shrink-0 ${t.icon}`}>
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-black text-sm tracking-tight truncate">{c.title}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{c.desc}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {c.combo.map((cb) => (
                        <Link
                          key={cb.to}
                          to={cb.to}
                          className="flex items-center justify-center gap-1.5 h-9 rounded-lg border border-brand-gold/40 bg-brand-gold/5 hover:bg-brand-gold hover:text-[#0F172A] text-[10px] uppercase tracking-widest font-black transition-colors"
                          data-testid={`cat-combo-${cb.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {cb.label} <ArrowRight className="w-3 h-3" />
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={c.title}
                  to={c.to}
                  className="group flex items-center gap-3 p-4 rounded-xl border border-brand-gold/15 bg-card/50 hover:bg-card hover:border-brand-gold/40 hover:-translate-y-0.5 transition-all"
                  data-testid={`cat-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className={`w-11 h-11 rounded-lg border flex items-center justify-center shrink-0 ${t.icon}`}>
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-black text-sm tracking-tight truncate">{c.title}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{c.desc}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-brand-gold group-hover:translate-x-0.5 transition-all" />
                </Link>
              );
            })}
          </div>
        </div>

        {/* ============ PROGRESS CHART ============ */}
        <div className="mb-12 rounded-2xl p-6 sm:p-8 metal-border bg-card/40">
          <ProgressChart />
        </div>

        {/* ============ STREAK CALENDAR ============ */}
        <div className="mb-12">
          <StreakCalendar days={365} />
        </div>

        {/* ============ MODULES BENTO ============ */}
        <div className="mb-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2">Start a session</div>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Choose your module</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULES.map((m, i) => {
              const Icon = m.icon;
              const spotlight = !!m.spotlight;
              const a = ACCENT_CLASSES[m.accent] || ACCENT_CLASSES.gold;
              return (
                <Link
                  key={m.to}
                  to={m.to}
                  data-testid={`dashboard-module-${i}`}
                  className={`group relative rounded-2xl p-8 overflow-hidden transition-all hover:-translate-y-1 border ${
                    spotlight
                      ? "bg-gradient-to-br from-brand-crimson/15 via-brand-gold/8 to-transparent border-brand-crimson/50"
                      : `bg-card/40 border-brand-gold/15 ${a.ring}`
                  }`}
                >
                  {a.strip && <div className={`absolute top-0 left-0 right-0 h-0.5 ${a.strip}`} />}
                  {spotlight && (
                    <div className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-crimson text-white text-[9px] uppercase tracking-widest font-black">
                      <Crown className="w-2.5 h-2.5" /> Featured
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground mb-6">{m.tag}</div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 border transition-transform group-hover:scale-105 ${a.icon}`}>
                    <Icon className="w-5 h-5" strokeWidth={2} />
                  </div>
                  <h3 className="font-display text-2xl font-black tracking-tight mb-2">{m.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                  <div className="mt-6 flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-brand-gold group-hover:gap-3 transition-all">
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* ============ REFERRAL CARD ============ */}
        <ReferralCard />

        {/* ============ BADGES ============ */}
        <div className="my-12 rounded-2xl p-6 sm:p-8 metal-border bg-card/40">
          <BadgesGrid limit={8} showViewAll />
        </div>

        {/* ============ ADAPTIVE PICKS ============ */}
        {stats?.by_module && Object.keys(stats.by_module).length > 0 && (
          <div className="my-12" data-testid="dashboard-adaptive-picks">
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2">Tailored just for you</div>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Adaptive picks</h2>
            </div>
            <div className="space-y-4">
              {Object.keys(stats.by_module).map((m) => (
                <div key={m} className="rounded-2xl p-6 metal-border bg-card/40">
                  <AdaptivePicks module={m} limit={3} compact title={`Recommended · ${m}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ PER-MODULE SUMMARY ============ */}
        {stats?.by_module && Object.keys(stats.by_module).length > 0 && (
          <div className="mt-12">
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-2">Overview</div>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight flex items-center gap-3">
                Per-module summary <Trophy className="w-6 h-6 text-brand-gold" />
              </h2>
            </div>
            <div className="rounded-2xl overflow-hidden metal-border bg-card/40">
              <table className="w-full text-sm font-mono">
                <thead className="bg-secondary/60">
                  <tr>
                    <th className="text-left px-6 py-4 text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Module</th>
                    <th className="text-right px-6 py-4 text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Attempts</th>
                    <th className="text-right px-6 py-4 text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Avg Accuracy</th>
                    <th className="text-right px-6 py-4 text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Avg WPM</th>
                  </tr>
                </thead>
                <tbody data-testid="dashboard-breakdown">
                  {Object.entries(stats.by_module).map(([m, v]) => (
                    <tr key={m} className="border-t border-brand-gold/10">
                      <td className="px-6 py-4 capitalize font-bold text-foreground">{m}</td>
                      <td className="px-6 py-4 text-right">{v.count}</td>
                      <td className="px-6 py-4 text-right"><span className={v.avg_accuracy >= 90 ? "text-brand-emerald font-bold" : ""}>{v.avg_accuracy}%</span></td>
                      <td className="px-6 py-4 text-right text-brand-gold font-bold">{v.avg_wpm}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function MiniPromoCard({ icon: Icon, label, value, sub, tone = "gold", isFire }) {
  const tones = {
    gold: "border-brand-gold/40 bg-brand-gold/10 text-brand-gold",
    emerald: "border-brand-emerald/40 bg-brand-emerald/10 text-brand-emerald",
    crimson: "border-brand-crimson/40 bg-brand-crimson/10 text-brand-crimson",
    blue: "border-blue-500/40 bg-blue-500/10 text-blue-400",
  };
  return (
    <div className="relative rounded-xl border border-white/15 bg-white/[0.05] backdrop-blur-sm p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${tones[tone]}`}>
          <Icon className={`w-4 h-4 ${isFire ? "flame-pulse" : ""}`} strokeWidth={2.5} />
        </div>
        <div className="text-[9px] uppercase tracking-widest font-bold text-white/60">{label}</div>
      </div>
      <div className="font-mono text-2xl font-black tracking-tight text-white">{value}</div>
      {sub && <div className="text-[10px] font-mono text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}
